import { AnalyticsStepType, DateMacro, Program } from '@citruscamps/citrus-client'
import { DateTime, Duration, DurationUnits, Interval } from 'luxon'
import { isAnalyticsStep } from '../components/DateMacroSelect/components/DateSelectContainer'
import { IDateMacroQuery } from '../interfaces/DateMacroQuery'

export const isDateMacro = (value: any): value is DateMacro => {
  return new Array<DateMacro>(
    'last7days',
    'last30days',
    'last3months',
    'last6months',
    'thismonth',
    'thisquarter',
    'thisyear',
    'lastmonth',
    'lastquarter',
    'lastyear',
    'alltime',
    'custom',
  ).includes(value)
}

export const filterStepsByDuration = (duration: Duration): AnalyticsStepType[] => {
  if (duration.as('days') <= 1) {
    return ['hourly']
  } else if (duration.as('days') <= 7) {
    return ['hourly', 'daily']
  } else if (duration.as('months') < 2) {
    return ['daily', 'weekly']
  } else if (duration.as('months') < 3) {
    return ['daily', 'weekly', 'monthly']
  } else if (duration.as('months') < 12) {
    return ['weekly', 'monthly']
  } else {
    return ['monthly', 'yearly']
  }
}

export const getIntervalByDateMacro = (
  query: IDateMacroQuery,
  program?: Pick<Program, 'timezone' | 'created'>,
): Interval => {
  const today: DateTime = DateTime.local().endOf('day')
  if (query.date_macro === 'custom' && query.start_date && query.end_date) {
    return Interval.fromDateTimes(
      DateTime.fromJSDate(query.start_date)
        .setZone(query.timezone || program?.timezone)
        .startOf('day'),
      DateTime.fromJSDate(query.end_date)
        .setZone(query.timezone || program?.timezone)
        .endOf('day'),
    )
  } else if (query.date_macro === 'last30days') {
    const startDate: DateTime = today.startOf('day').plus({ day: 1 }).minus({ days: 30 })
    return Interval.fromDateTimes(startDate, today)
  } else if (query.date_macro === 'thismonth') {
    const startDate: DateTime = today.startOf('month')
    return Interval.fromDateTimes(startDate, today)
  } else if (query.date_macro === 'thisquarter') {
    const startDate: DateTime = today.startOf('quarter')
    return Interval.fromDateTimes(startDate, today)
  } else if (query.date_macro === 'thisyear') {
    const startDate: DateTime = today.startOf('year')
    return Interval.fromDateTimes(startDate, today)
  } else if (query.date_macro === 'alltime') {
    const startDate: DateTime = program
      ? (typeof program.created === 'string'
          ? DateTime.fromISO(program.created, {
              zone: program.timezone,
            })
          : DateTime.fromJSDate(program.created, {
              zone: program.timezone,
            })
        ).startOf('year')
      : DateTime.fromObject({ year: 2020 }).setZone(query.timezone).startOf('year')
    return Interval.fromDateTimes(startDate, today)
  } else if (query.date_macro === 'last7days') {
    const startDate: DateTime = today.startOf('day').plus({ day: 1 }).minus({ days: 7 })
    return Interval.fromDateTimes(startDate, today)
  } else if (query.date_macro === 'last3months') {
    const startDate: DateTime = today.startOf('day').plus({ day: 1 }).minus({ months: 3 })
    return Interval.fromDateTimes(startDate, today)
  } else if (query.date_macro === 'last6months') {
    const startDate: DateTime = today.startOf('day').plus({ day: 1 }).minus({ months: 6 })
    return Interval.fromDateTimes(startDate, today)
  }
  throw new Error('Unsupported date macro.')
}

export const getPastIntervalByDateMacro = (
  query: IDateMacroQuery,
  program?: Pick<Program, 'timezone' | 'created'>,
): Interval => {
  const interval: Interval | undefined = getIntervalByDateMacro(query, program)
  if (query.date_macro === 'thismonth') {
    const startDate: DateTime | undefined = interval.start?.minus({ month: 1 })
    return interval.set({
      start: startDate,
      end: startDate?.plus(interval.toDuration()),
    })
  } else if (query.date_macro === 'thisquarter') {
    const startDate: DateTime | undefined = interval.start?.minus({ quarter: 1 })
    return interval.set({
      start: startDate,
      end: startDate?.plus(interval.toDuration()),
    })
  } else if (query.date_macro === 'thisyear') {
    const startDate: DateTime | undefined = interval.start?.minus({ year: 1 })
    return interval.set({
      start: startDate,
      end: startDate?.plus(interval.toDuration()),
    })
  }
  return interval.set({
    start: interval.start?.minus(interval.toDuration().plus({ milliseconds: 1 })),
    end: interval.end?.minus(interval.toDuration().plus({ milliseconds: 1 })),
  })
}

export const getIntervalByUnit = (query: IDateMacroQuery): Interval | undefined => {
  const { start_date, end_date, unit, timezone } = query
  if (!start_date || !end_date || !unit) {
    return undefined
  }
  const startDate: DateTime = DateTime.fromJSDate(start_date).setZone(timezone)
  const endDate: DateTime = DateTime.fromJSDate(end_date).setZone(timezone)
  let range: DurationUnits | undefined
  switch (unit) {
    case 'hourly':
    case 'daily':
      range = 'day'
      break
    case 'weekly':
      range = 'week'
      break
    case 'monthly':
      range = 'month'
      break
    case 'yearly':
      range = 'year'
      break
  }
  if (range === 'day') {
    return Interval.fromDateTimes(startDate.startOf('day'), endDate.endOf('day'))
  }
  const hasStartDateMatchingRangeStart: boolean =
    !!range && startDate.startOf('day') === startDate.startOf(range)
  const hasEndDateMatchingRangeEnd: boolean =
    !!range &&
    [endDate.endOf(range), DateTime.local().setZone(timezone).endOf('day')].includes(
      endDate.endOf('day'),
    )
  const isFuture = !!range && endDate.endOf(range) > DateTime.local().setZone(timezone).endOf('day')
  const isRangeDifferent = !hasStartDateMatchingRangeStart || !hasEndDateMatchingRangeEnd
  if (isRangeDifferent && isFuture) {
    return Interval.fromDateTimes(
      startDate.startOf(range),
      DateTime.local().setZone(timezone).endOf('day'),
    )
  }
  if (isRangeDifferent) {
    return Interval.fromDateTimes(startDate.startOf(range), endDate.endOf(range).setZone(timezone))
  }
  return Interval.fromDateTimes(startDate, endDate)
}

export const getIntervalByQuery = (query: IDateMacroQuery, program?: Program): Interval => {
  const defaultValue = getIntervalByDateMacro(query, program)
  if (
    !isDateMacro(query.date_macro) ||
    !isAnalyticsStep(query.unit) ||
    (query.date_macro === 'custom' && !query.start_date && !query.end_date)
  ) {
    return getIntervalByDateMacro(query, program)
  }
  const interval: Interval = getIntervalByDateMacro(query, program)
  const steps = interval && filterStepsByDuration(interval?.toDuration())
  return (
    getIntervalByUnit({
      ...query,
      start_date: interval?.start?.toJSDate(),
      end_date: interval?.end?.toJSDate(),
      unit: steps?.find((s) => s === query.unit) || steps?.[0] || query.unit,
    }) || defaultValue
  )
}

export const getPastIntervalByQuery = (query: IDateMacroQuery, program?: Program): Interval => {
  const defaultValue = getPastIntervalByDateMacro(query, program)
  if (
    !isDateMacro(query.date_macro) ||
    !isAnalyticsStep(query.unit) ||
    (query.date_macro === 'custom' && !query.start_date && !query.end_date)
  ) {
    return defaultValue
  }

  let currentInterval: Interval = getIntervalByDateMacro(query, program)
  let steps = currentInterval && filterStepsByDuration(currentInterval?.toDuration())
  const unit = steps?.find((s) => s === query.unit) || steps?.[0] || query.unit
  currentInterval =
    getIntervalByUnit({
      ...query,
      start_date: currentInterval?.start?.toJSDate(),
      end_date: currentInterval?.end?.toJSDate(),
      unit,
    }) || currentInterval
  let interval: Interval = getPastIntervalByDateMacro(query)
  interval =
    getIntervalByUnit({
      ...query,
      start_date: interval?.start?.toJSDate(),
      end_date: interval?.end?.toJSDate(),
      unit,
    }) || interval
  let unitConversion: DurationUnits
  switch (unit) {
    case 'hourly':
      unitConversion = 'hours'
      break
    case 'daily':
      unitConversion = 'days'
      break
    case 'weekly':
      unitConversion = 'weeks'
      break
    case 'monthly':
      unitConversion = 'months'
      break
    case 'yearly':
      unitConversion = 'years'
      break
    default:
      unitConversion = 'days'
      break
  }
  const currentDuration = currentInterval?.length(unitConversion)
  let pastDuration = interval?.length(unitConversion)
  while (
    currentDuration &&
    pastDuration &&
    Math.ceil(currentDuration) !== Math.ceil(pastDuration)
  ) {
    const durationDifference = Math.ceil(currentDuration) - Math.ceil(pastDuration)
    const durationToAdd = Duration.fromObject({ [unitConversion]: Math.abs(durationDifference) })
    if (durationDifference > 0) {
      interval =
        getIntervalByUnit({
          ...query,
          start_date: interval?.start?.toJSDate(),
          end_date: interval?.end?.plus(durationToAdd).toJSDate(),
          unit,
        }) || interval
    } else if (durationDifference < 0) {
      interval =
        getIntervalByUnit({
          ...query,
          start_date: interval?.start?.plus(durationToAdd).toJSDate(),
          end_date: interval?.end?.toJSDate(),
          unit,
        }) || interval
    }
    pastDuration = interval?.length(unitConversion)
  }
  steps = filterStepsByDuration(interval?.toDuration())
  return (
    getIntervalByUnit({
      ...query,
      start_date: interval?.start?.toJSDate(),
      end_date: interval?.end?.toJSDate(),
      unit: steps?.find((s) => s === query.unit) || steps?.[0] || query.unit,
    }) || defaultValue
  )
}
